import { useEffect, useRef, useState } from "react";
import { FileUp, Loader2, Paperclip, Sparkles, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const EMPTY = {
  vendor: "", quote_ref: "", amount: "", cost_code_id: "", cost_type: "M",
  quote_date: "", valid_through: "", notes: "", section: "",
  manufacturer: "", freight_amount: "", freight_cost_code_id: "", quote_file_id: null,
};

export default function VendorQuoteDialog({
  open, onOpenChange, estimateId, targetLines, costCodes, costTypes, onApplied,
}) {
  const [form, setForm] = useState(EMPTY);
  const [quotes, setQuotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const [attached, setAttached] = useState(null);   // stored file record
  const [read, setRead] = useState(null);           // what the LLM pulled out
  const fileRef = useRef(null);
  const spread = (targetLines || []).length > 0;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setAttached(null);
    setRead(null);
    estApi.getQuotes(estimateId).then(setQuotes).catch(() => setQuotes([]));
  }, [open, estimateId]);

  const upload = async (file) => {
    if (!file) return;
    setReading(true);
    try {
      const res = await estApi.uploadQuoteFile(estimateId, file);
      const x = res.extracted || {};
      setAttached(res.file);
      setRead(x);
      // Everything below stays editable — the read is a starting point, not the truth.
      setForm((f) => ({
        ...f,
        vendor: x.vendor || f.vendor,
        manufacturer: x.manufacturer || x.vendor || "",
        quote_ref: x.quote_ref || f.quote_ref,
        amount: x.total ? String(round2(x.total - (x.freight || 0))) : f.amount,
        freight_amount: x.freight ? String(x.freight) : "",
        quote_date: x.quote_date || f.quote_date,
        valid_through: x.valid_through || f.valid_through,
        notes: [x.notes, x.lead_time && `Lead time: ${x.lead_time}`]
          .filter(Boolean).join("\n") || f.notes,
        quote_file_id: res.file.id,
      }));
      toast.success("Quote read — check the numbers before applying");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that quote");
    } finally {
      setReading(false);
    }
  };

  const submit = async () => {
    if (!form.vendor.trim()) { toast.error("Vendor is required"); return; }
    setSaving(true);
    try {
      const res = await estApi.addQuote(estimateId, {
        vendor: form.vendor.trim(), quote_ref: form.quote_ref, amount: parseFloat(form.amount) || 0,
        cost_code_id: form.cost_code_id || null, cost_type: form.cost_type,
        quote_date: form.quote_date || null, valid_through: form.valid_through || null,
        notes: form.notes, section: form.section,
        manufacturer: form.manufacturer, quote_file_id: form.quote_file_id,
        freight_amount: parseFloat(form.freight_amount) || 0,
        freight_cost_code_id: form.freight_cost_code_id || null,
        applies_to: spread ? "allocated_to_lines" : "lump_sum_line",
        line_ids: spread ? targetLines.map((l) => l.id) : [],
      });
      onApplied(res);
      toast.success(spread ? "Quote spread across the selected lines" : "Quote added as a lump-sum line");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the quote");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const res = await estApi.deleteQuote(estimateId, id);
    setQuotes((q) => q.filter((x) => x.id !== id));
    onApplied(res);
    toast.success("Quote removed");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="vendor-quote-dialog">
        <DialogHeader><DialogTitle>Vendor quote</DialogTitle></DialogHeader>
        <p className="text-[11px] text-slate-500" data-testid="quote-mode-note">
          {spread
            ? `Spreading this quote across ${targetLines.length} selected line(s) by quantity.`
            : "No lines selected — this quote will be added as its own lump-sum line."}
        </p>

        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3">
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.docx,.txt,.csv"
                 data-testid="quote-file-input"
                 onChange={(e) => upload(e.target.files?.[0])} />
          {!attached ? (
            <button onClick={() => fileRef.current?.click()} disabled={reading}
                    data-testid="quote-upload-btn"
                    className="flex w-full items-center justify-center gap-2 text-xs text-slate-600 hover:text-slate-900">
              {reading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              {reading ? "Reading the quote…" : "Drop in the vendor's quote (PDF or DOCX) and I'll read it"}
            </button>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-slate-700">
                <Paperclip className="h-3.5 w-3.5 text-slate-400" />
                <span className="truncate font-medium" data-testid="quote-file-name">
                  {attached.file_name}
                </span>
                <button onClick={async () => {
                        const id = attached.id;
                        setAttached(null); setRead(null);
                        setForm((f) => ({ ...f, quote_file_id: null }));
                        try { await estApi.deleteQuoteFile(id); } catch { /* row already gone */ }
                      }}
                        className="ml-auto text-[10px] text-slate-400 hover:text-red-500"
                        data-testid="quote-file-clear">remove</button>
              </div>
              {read && (
                <p className="flex items-start gap-1.5 text-[10px] text-emerald-700"
                   data-testid="quote-read-summary">
                  <Sparkles className="mt-0.5 h-3 w-3 shrink-0" />
                  Read {read.line_items?.length || 0} line item(s) · total {money(read.total)}
                  {read.freight ? ` · freight ${money(read.freight)}` : " · no freight found"}.
                  Correct anything below before applying.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="text-[11px] text-slate-500">Vendor *</label>
            <Input value={form.vendor} onChange={(e) => set("vendor", e.target.value)}
                   className="h-8 text-xs" data-testid="quote-vendor" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Quote ref</label>
            <Input value={form.quote_ref} onChange={(e) => set("quote_ref", e.target.value)}
                   className="h-8 text-xs" data-testid="quote-ref" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Amount</label>
            <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                   className="h-8 text-right text-xs" data-testid="quote-amount" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Manufacturer</label>
            <Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)}
                   className="h-8 text-xs" data-testid="quote-manufacturer" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Freight (own line)</label>
            <Input type="number" step="0.01" value={form.freight_amount}
                   onChange={(e) => set("freight_amount", e.target.value)}
                   className="h-8 text-xs" data-testid="quote-freight" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Freight cost code</label>
            <select value={form.freight_cost_code_id}
                    onChange={(e) => set("freight_cost_code_id", e.target.value)}
                    className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs"
                    data-testid="quote-freight-cost-code">
              <option value="">— same as the quote —</option>
              {(costCodes || []).map((c) => (
                <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Cost type</label>
            <select value={form.cost_type} onChange={(e) => set("cost_type", e.target.value)}
                    className="h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs"
                    data-testid="quote-cost-type">
              {(costTypes || []).map((t) => (
                <option key={t.id} value={t.code}>{t.code} — {t.name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] text-slate-500">Cost code</label>
            <select value={form.cost_code_id} onChange={(e) => set("cost_code_id", e.target.value)}
                    className="h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs"
                    data-testid="quote-cost-code">
              <option value="">No cost code</option>
              {(costCodes || []).map((c) => (
                <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Quote date</label>
            <Input type="date" value={form.quote_date} onChange={(e) => set("quote_date", e.target.value)}
                   className="h-8 text-xs" data-testid="quote-date" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Valid through</label>
            <Input type="date" value={form.valid_through}
                   onChange={(e) => set("valid_through", e.target.value)}
                   className="h-8 text-xs" data-testid="quote-valid-through" />
          </div>
          {!spread && (
            <div className="col-span-2">
              <label className="text-[11px] text-slate-500">Section (optional)</label>
              <Input value={form.section} onChange={(e) => set("section", e.target.value)}
                     className="h-8 text-xs" data-testid="quote-section" />
            </div>
          )}
          <div className="col-span-2">
            <label className="text-[11px] text-slate-500">Notes</label>
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)}
                   className="h-8 text-xs" data-testid="quote-notes" />
          </div>
        </div>

        {quotes.length > 0 && (
          <div className="rounded border border-slate-200" data-testid="quote-list">
            {quotes.map((q) => (
              <div key={q.id} className="flex items-center gap-2 border-b border-slate-100 px-2 py-1.5 text-[11px] last:border-0">
                <span className="flex-1 truncate text-slate-700">{q.vendor} {q.quote_ref}</span>
                <span className="tabular-nums text-slate-600">{money(q.amount)}</span>
                <button onClick={() => remove(q.id)} className="text-slate-400 hover:text-red-600"
                        data-testid={`quote-delete-${q.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="quote-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="quote-submit">
            {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />} Save quote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
